<template>
  <Dashboard>
    <div class="flex flex-col gap-12">
      <div v-if="!coachIsLoading && user.match_token_outdated && !hideMatchTokenWarning" class="-mb-5 flex w-full bg-gray-800 rounded-lg px-3 py-3 gap-2">
        <img src="../assets/icons/error.svg" class="w-5 h-5">
        <i18n-t scope="global" keypath="home.dashboard.match_token_outdated" tag="h1" class="text-text-primary font-light text-sm flex-1">
          <template #link><span class="underline cursor-pointer" @click="showUpdateMatchTokenModal = true">{{ $t('home.dashboard.match_token_outdated_link') }}</span></template>
        </i18n-t>
        <button @click="hideWarning('match_token')" class="text-text-secondary text-sm hover:text-text-primary">{{ $t('common.dismiss') }}</button>
      </div>

      <div v-if="!coachIsLoading && !userSettingsLoading && (!userSettings?.match_auth_token || userSettings?.match_auth_token === '********') && !hideAuthTokenWarning" class="-mb-5 flex w-full bg-gray-800 rounded-lg px-3 py-3 gap-2">
        <img src="../assets/icons/error.svg" class="w-5 h-5">
        <i18n-t scope="global" keypath="home.dashboard.auth_code_missing" tag="h1" class="text-text-primary font-light text-sm flex-1">
          <template #link><span class="underline cursor-pointer" @click="showUpdateMatchAuthTokenModal = true">{{ $t('home.dashboard.auth_code_missing_link') }}</span></template>
        </i18n-t>
        <button @click="hideWarning('auth_token')" class="text-text-secondary text-sm hover:text-text-primary">{{ $t('common.dismiss') }}</button>
      </div>

      <CoachSection
        :user="user"
        :showMatches="showMatches"
        :userStatsHistory="userStatsHistory"
        :userSettings="userSettings"
        @launchRecommendedMod="launchRecommendedMod"
        @startServer="updateModalServerInfo"
        @loadingStateChanged="handleCoachLoadingStateChange"
      />

      <div v-if="customRoutines && customRoutines.length > 0" class="flex flex-col gap-6">
        <!-- Title and Subtitle Row -->
        <div class="flex gap-4">
          <div class="flex flex-col gap-2 flex-1">
            <div class="flex items-center justify-between">
              <h1 class="text-text-primary font-semibold text-[24px]">BLAST Premier Rivals</h1>
            </div>
            <p class="text-text-primary text-sm font-light">
              {{ $t('home.dashboard.blast_subtitle') }}
            </p>
          </div>

          <button @click="hideWarning('blast_sponsorship')" class="text-text-secondary underline text-sm hover:text-text-primary">
            {{ hideBlastSponsorship ? $t('home.dashboard.show') : $t('home.dashboard.hide') }}
          </button>
        </div>
      </div>

      <div class="flex flex-col gap-6">
        <h1 class="text-text-primary font-semibold text-[24px]">{{ $t('home.dashboard.whats_new') }}</h1>
        <div
          v-if="recentNews.length == 0"
          class="bg-gray-900 p-4 text-text-secondary text-xs tracking-[1.5px] w-full"
        >
          {{ $t('home.dashboard.nothing_new') }}
        </div>
        <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div
            v-for="(post, index) in recentNews"
            class="flex flex-col bg-gray-900 rounded-lg p-4 cols-span-1 w-full gap-6"
          >
            <img
              class="w-full aspect-[16/9] object-cover rounded-lg"
              :src="post.image_url"
            />
            <div class="flex flex-col gap-3 flex-1">
              <h2
                class="text-text-secondary font-light text-[10px] uppercase tracking-[1.5px]"
              >
                {{ $t('home.dashboard.new_content', { type: getContentTypeText(post?.content_type) }) }}
              </h2>
              <h1 class="text-text-primary font-medium text-lg">{{ post?.title }}</h1>
              <p class="text-text-secondary font-light text-sm flex-1">
                {{ post?.description }}
              </p>
            </div>
            <button
              @click="executePostAction(post)"
              class="py-3 w-full rounded-lg text-text-primary text-xs font-medium duration-300 flex items-center justify-center gap-2"
              :class="[
                index === 0
                  ? 'bg-primary-600 hover:bg-primary-500'
                  : 'bg-gray-800 hover:bg-gray-700',
              ]"
            >
              {{ post.button_text }}
              <svg
                v-if="launchLoading === post.id"
                width="16"
                height="16"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="animate-spin"
              >
                <circle cx="16" cy="16" r="15" stroke="#1E2733" stroke-width="2" />
                <path
                  d="M16 1C17.9698 1 19.9204 1.38799 21.7403 2.14181C23.5601 2.89563 25.2137 4.00052 26.6066 5.3934C27.9995 6.78628 29.1044 8.43986 29.8582 10.2597C30.612 12.0796 31 14.0302 31 16"
                  stroke="#4E54C8"
                  stroke-width="2"
                />
              </svg>
              <HandThumbUpIcon
                v-if="launchSuccess === post.id"
                class="w-4 h-4 text-success-400"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  </Dashboard>
  <StartServerModal
    :server="serverInfo"
    :show="showServerModal"
    :showMapSelector="showMapSelector"
    @close="showServerModal = false"
  />
  <!-- Allstar highlights disabled
  <MatchHighlightsModal
    :show="showMatchHighlightsModal"
    :clipId="selectedMatchHighlightId"
    @close="closeMatchHighlightModal()"
  >
  </MatchHighlightsModal>
  -->
  <UpdateMatchTokenModal
    :show="showUpdateMatchTokenModal"
    @close="showUpdateMatchTokenModal = false"
  />
  <UpdateMatchAuthTokenModal
    :show="showUpdateMatchAuthTokenModal"
    @close="showUpdateMatchAuthTokenModal = false"
  />
</template>

<script setup>
import {
  ref, watch, computed, onMounted, onUnmounted, getCurrentInstance,
} from 'vue';
import { Chart, registerables } from 'chart.js';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import {
  HandThumbUpIcon, ArrowRightIcon, ClockIcon, PlayIcon,
} from '@heroicons/vue/20/solid';
import moment from 'moment';
import { useHead } from '@unhead/vue';
import {
  retrievalStartedMessage,
  retrievalFailedMessage,
  messageDismissDelay,
  SHORT_DISMISS_MS,
} from '../utils/match_retrieval';
import MatchRow from '../components/UserProfile/MatchRow.vue';
import StartServerModal from '../components/StartServerModal.vue';
import Pill from '../components/Pill.vue';
// import MatchHighlightsModal from "../components/MatchHighlightsModal.vue"; // Allstar highlights disabled
import CoachSection from '../components/Home/CoachSection.vue';
import Dashboard from '../components/layouts/DashboardLayout.vue';
import { createClickablePointsPlugin } from '../utils/chartPlugins';
import { fetchCoachMatchHistory } from '../utils/coach_pipeline';
import UpdateMatchTokenModal from '../components/UpdateMatchTokenModal.vue';
import UpdateMatchAuthTokenModal from '../components/UpdateMatchAuthTokenModal.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const auth = useAuth();
const { t } = useI18n();
const recentNews = ref([]);
const userWinPercentage = ref([]);
const showServerModal = ref(false);
const serverInfo = ref();
const activeServer = computed(() => store.getters.activeServer);
const launchLoading = ref(false);
const launchSuccess = ref(false);
const matches = ref([]);
const userElos = ref(null);
const loadingMatches = ref(false);
// const selectedMatchHighlightId = ref(null); // Allstar highlights disabled
// const showMatchHighlightsModal = ref(false);
const showMapSelector = ref(false);
const userStatsHistory = ref([]);
const recommendedMap = ref(null);
// vue-auth replaces state.data wholesale on every /me, so a ref(auth.user())
// snapshot goes stale for access_level and match_token_outdated too. It also
// nulls state.data on logout and on a 401 revalidation, and the reactivity flush
// runs before the router redirect — so default to {} rather than let the
// template read match_token_outdated off null and blank the dashboard.
const user = computed(() => auth.user() || {});
const showUpdateMatchTokenModal = ref(false);
const mostPlayedMap = ref(null);
const totalMatchesCount = ref(0);
const totalPageCount = ref(0);
const userSettings = ref(null);
const userSettingsLoading = ref(true);
const { proxy } = getCurrentInstance();
const cookies = proxy.$cookies;
const showUpdateMatchAuthTokenModal = ref(false);
const hideMatchTokenWarning = ref(!!getCookie('hide_match_token_warning'));
const hideAuthTokenWarning = ref(!!getCookie('hide_auth_token_warning'));
const hideBlastSponsorship = ref(!!getCookie('hide_blast_sponsorship'));
const coachIsLoading = ref(true);
const customRoutines = ref([]);
const selectedRoutine = ref(null);
const blastTaskList = ref(null);
const windowWidth = ref(window.innerWidth);

useHead({
  title: () => t('home.dashboard.page_title'),
});

Chart.register(...registerables);

// Create the plugin
const clickablePointsPlugin = createClickablePointsPlugin(
  (index) => userStatsHistory.value[userStatsHistory.value.length - 1 - index]?.match_id,
  (matchId) => router.push(`/matches/${matchId}`),
);

const showMatches = computed(() => {
  const show_matches = store.state.siteSettings?.find(
    (setting) => setting.name === 'show_matches',
  )?.enabled;

  return show_matches;
});

// Register the plugin
Chart.register(...registerables, clickablePointsPlugin);

const featuredMods = computed(() => store.state.modInfo?.mods.filter((mod) => mod.featured));

// Track window width for responsive filler count
const updateWindowWidth = () => {
  windowWidth.value = window.innerWidth;
};

onMounted(() => {
  if (!cookies.get('has_logged_in')) {
    cookies.set('has_logged_in', true, '365d', null, '.refrag.gg');
  }

  if (route.query.game_mode) {
    router.push({
      path: '/play',
      query: {
        game_mode: route.query.game_mode,
        map: route.query.map || route.query.cs_map,
      },
    });
    return;
  }

  window.addEventListener('resize', updateWindowWidth);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateWindowWidth);
  Chart.unregister(clickablePointsPlugin);
});

function launchRecommendedMod(mod, showMap = false, ttk = null, map = null) {
  if (showMap) {
    showMapSelector.value = true;
    serverInfo.value = {
      mode: mod,
      time_to_kill: ttk,
    };
    showServerModal.value = true;
  } else {
    showMapSelector.value = false;
    serverInfo.value = {
      mode: mod,
      cs_map: map || recommendedMap.value,
      time_to_kill: ttk,
    };
    showServerModal.value = true;
  }
}

// Bumped per fetch so an overlapping request can't land a stale payload.
let matchStatsRequestSeq = 0;

function getRecentMatchStats() {
  matchStatsRequestSeq += 1;
  const seq = matchStatsRequestSeq;
  userStatsHistory.value = [];

  fetchCoachMatchHistory(user.value, { platform: user.value.platform })
    .then(({ matches, totalCount, totalPages }) => {
      if (seq !== matchStatsRequestSeq) return;

      userStatsHistory.value = matches;
      totalMatchesCount.value = totalCount;
      totalPageCount.value = totalPages;
      calculateRecentStats();
    }).catch((error) => {
      if (seq !== matchStatsRequestSeq) return;

      console.error('Failed to load recent match stats:', error);
    });
}

watch(() => store.getters.modInfo, () => {
  if (recommendedMap.value) return;

  recommendedMap.value = store.getters.modInfo?.mods[0]?.cs_maps.find((map) => map.name === mostPlayedMap.value) || store.getters.modInfo?.mods[0]?.cs_maps[0];
});

getRecentMatchStats();

function launchFeaturedMod(mod) {
  if (!activeServer.value?.id) {
    launchLoading.value = null;
    showMapSelector.value = true;
    serverInfo.value = {
      mode: mod.name,
    };
    showServerModal.value = true;
    return;
  }

  if (activeServer.value.status !== 'online') {
    store.commit('showMessage', {
      messageType: 'error',
      messageText: t('home.dashboard.server_not_online_mode'),
    });
    return;
  }

  if (!launchLoading.value) {
    launchLoading.value = mod.id;
  }
  axios({
    url: `/cs_servers/${activeServer.value.id}/exec_server_command`,
    method: 'POST',
    params: {
      command: 'change_mod',
      value: mod.name,
    },
    headers: {
      'X-TEAM-ID': store.getters.activeTeam.id,
    },
  })
    .then(() => {
      launchSuccess.value = launchLoading.value;
      launchLoading.value = null;
      setTimeout(() => {
        launchSuccess.value = null;
      }, 3000);
    })
    .catch((err) => {
      launchLoading.value = null;
      store.commit('showMessage', {
        messageType: 'error',
        messageText: t('home.dashboard.server_unreachable'),
      });
      setTimeout(() => {
        store.commit('hideMessage');
      }, 3000);
    });
}

function updateModalServerInfo(launchSettings) {
  serverInfo.value = launchSettings;
  showServerModal.value = true;
}

// Allstar highlights disabled
// function closeMatchHighlightModal() {
//   showMatchHighlightsModal.value = false;
// }

function getModName(type) {
  if (type === 'set') {
    return 'Creator Studio';
  } if (type === 'grenade group') {
    return 'Grenade Groups';
  } if (type === 'strategy') {
    return 'Strategies';
  }
}

function launchPostContent(post, type) {
  const { content } = post;
  if (!activeServer.value?.id) {
    const launchSettings = {
      cs_map: content.cs_map,
      mode: getModName(type),
      content,
    };
    updateModalServerInfo(launchSettings);
    return;
  }

  let command = '';
  let value = '';

  if (type === 'set') {
    command = 'load_set';
    value = `custom_set, ${content.id}`;
  } else if (type === 'grenade group') {
    command = 'load_nadr';
    value = `grenades, ${content.id}`;
  } else if (type === 'strategy') {
    command = 'load_nadr';
    value = `strat, ${content.id}`;
  }

  if (activeServer.value.status !== 'online') {
    store.commit('showMessage', {
      messageType: 'error',
      messageText: t('home.dashboard.server_not_online_routine'),
    });
    return;
  }

  launchLoading.value = post.id;

  axios({
    url: `/cs_servers/${activeServer.value.id}/exec_server_command`,
    method: 'POST',
    params: {
      command,
      value,
    },
    headers: {
      'X-TEAM-ID': store.getters.activeTeam.id,
    },
  })
    .then(() => {
      launchLoading.value = null;
      launchSuccess.value = post.id;
      setTimeout(() => {
        launchSuccess.value = null;
      }, 3000);
    })
    .catch((err) => {
      store.commit('showMessage', {
        messageType: 'error',
        messageText: t('home.dashboard.server_unreachable'),
      });
      setTimeout(() => {
        store.commit('hideMessage');
      }, 3000);
      launchLoading.value = null;
    });
}

function executePostAction(post) {
  window.posthog?.capture('home_post_clicked', {
    post_id: post.id,
    content_type: post.content_type,
    content_title: post.title,
  });
  switch (post.content_type) {
    case 'feature':
      router.push(`${post.content_link}`);
      break;
    case 'CustomSet':
      launchPostContent(post, 'set');
      break;
    case 'GrenadeGroup':
      launchPostContent(post, 'grenade group');
      break;
    case 'blog':
      window.open(post.content_link, '_blank');
      break;
    case 'external':
      window.open(post.content_link, '_blank');
      break;
    default:
      break;
  }
}

function getContentTypeText(type) {
  switch (type) {
    case 'feature':
      return t('home.content_type.feature');
    case 'external':
      return t('home.content_type.external');
    case 'CustomSet':
      return t('home.content_type.custom_set');
    case 'GrenadeGroup':
      return t('home.content_type.grenade_group');
    case 'blog':
      return t('home.content_type.blog');
    default:
      return t('home.content_type.news');
  }
}

function retrieveMatches() {
  loadingMatches.value = true;
  let dismissAfter = SHORT_DISMISS_MS;
  axios
    .post('/matches/retrieve_my_matches')
    .then((response) => {
      const message = retrievalStartedMessage(response);
      dismissAfter = messageDismissDelay(message);
      store.commit('showMessage', message);
    })
    .catch((error) => {
      const message = retrievalFailedMessage(error);
      dismissAfter = messageDismissDelay(message);
      store.commit('showMessage', message);
    })
    .finally(() => {
      loadingMatches.value = false;
      setTimeout(() => {
        store.commit('hideMessage');
      }, dismissAfter);
    });
}

axios
  .get('/news_posts')
  .then((response) => {
    recentNews.value = response.data;
  })
  .catch((error) => {
    console.error(error);
  });

axios
  .get('/matches/my_history')
  .then((response) => {
    matches.value = response.data;
    if (route.hash === '#matchhistory') {
      setTimeout(() => {
        document.getElementById('match-history').scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }, 100);
    }
  })
  .catch((error) => {
    console.error(error);
  });

axios
  .get('/users/my_settings')
  .then((response) => {
    userSettings.value = response.data;
    userSettingsLoading.value = false;
  })
  .catch((error) => {
    console.error(error);
  });

axios
  .get('statistics/my_elos')
  .then((response) => {
    userElos.value = response.data.elo_stats;
  })
  .catch((error) => {
    console.error(error);
  });

function calculateRecentStats() {
  const totalMatches = userStatsHistory.value.length;
  if (totalMatches === 0) return;

  const wins = userStatsHistory.value.filter((match) => match.match_result === 1).length;

  userWinPercentage.value = ((wins / totalMatches) * 100).toFixed(0);
}

function hideWarning(type) {
  if (type === 'match_token') {
    const cookieName = `hide_${type}_warning`;
    cookies.set(cookieName, 'true', '30d');
    hideMatchTokenWarning.value = true;
  } else if (type === 'auth_token') {
    const cookieName = `hide_${type}_warning`;
    cookies.set(cookieName, 'true', '30d');
    hideAuthTokenWarning.value = true;
  } else if (type === 'blast_sponsorship') {
    const cookieName = 'hide_blast_sponsorship';
    // Toggle the value
    hideBlastSponsorship.value = !hideBlastSponsorship.value;
    // Set cookie based on new value
    if (hideBlastSponsorship.value) {
      cookies.set(cookieName, 'true', '30d');
    } else {
      cookies.remove(cookieName);
    }
  }
}

function getCookie(name) {
  return cookies.get(name);
}

function handleCoachLoadingStateChange(isLoading) {
  coachIsLoading.value = isLoading;
}

function selectRoutine(routine) {
  selectedRoutine.value = routine;
}

function handleImageError(event) {
  event.target.style.display = 'none';
}

function getFillerCount() {
  const currentCount = customRoutines.value.length;
  let minTotal = 4; // Default minimum

  // Increase minimum on larger screens
  if (windowWidth.value >= 1536) { // 2xl breakpoint
    minTotal = 7;
  } else if (windowWidth.value >= 1280) { // xl breakpoint
    minTotal = 5;
  } else if (windowWidth.value >= 1024) { // lg breakpoint
    minTotal = 4;
  }

  return Math.max(0, minTotal - currentCount);
}

function launchCustomRoutine(section) {
  if (!section || !blastTaskList.value) return;

  // Launch the first task in the section, or the task list itself
  const taskListId = blastTaskList.value.id;
  const firstTask = section.tasks?.[0];

  // IF WE CHOOSE TO USE PRO, SET THIS TO 1:
  const useDifficultyPreset = 0;

  // Always show modal to allow difficulty selection (whether server exists or not)
  serverInfo.value = {
    mode: 'Creator Studio',
    content: {
      task_list_id: taskListId,
      task_list_section_id: section.id,
      id: firstTask?.id || 0, // Task ID for launch command
      is_exam: false,
      title: section.name,
      type: 'task',
      use_difficulty: 1, // Enable difficulty selector for Blast tasks
      task_list_type: 'playlist',
    },
  };
  showServerModal.value = true;
}

</script>

<style scoped>
.video-container-bg {
  background: linear-gradient(234deg, rgba(63, 29, 255, 0.00) 0%, rgba(160, 47, 158, 0.10) 43.19%, rgba(233, 60, 86, 0.20) 82.59%, rgba(255, 64, 64, 0.20) 98.72%), #0F141A;
  border-radius: 8px;
}
</style>
